// EXIF orientation applied while drawing, for decoders that hand back a photo's STORED pixels.
//
// The resize workers want an upright decode — createImageBitmap(file, {imageOrientation: 'from-image'}) or
// ImageDecoder — but Safari has no ImageDecoder, and Safari before 17 rejects 'from-image' outright
// ("TypeError: Type error"), so a rotated photo (portrait from most cameras and every iPhone) had no
// decode route there. A plain createImageBitmap of the same file works: these draw it upright.

/** A decoded image and the EXIF orientation still to apply when drawing it (1 = already upright). */
export interface Drawable {
  source: CanvasImageSource;
  /** The source's own size (the STORED size when `orientation` swaps the axes). */
  width: number;
  height: number;
  orientation: number;
  close(): void;
}

/** Orientations that swap width and height (5–8). */
export const swapsAxes = (orientation: number) => orientation >= 5 && orientation <= 8;

/** The upright size of a Drawable. */
export const uprightSize = (d: Drawable): { width: number; height: number } =>
  swapsAxes(d.orientation) ? { width: d.height, height: d.width } : { width: d.width, height: d.height };

/**
 * The canvas transform that draws a STORED image upright into a W×H (upright) canvas for an EXIF
 * orientation, as [a, b, c, d, e, f]; null for 1 (or anything unknown) = no transform. The image is then
 * drawn at (0, 0) with size (H, W) when the orientation swaps the axes, else (W, H).
 */
export function orientationTransform(orientation: number, W: number, H: number): [number, number, number, number, number, number] | null {
  switch (orientation) {
    case 2: return [-1, 0, 0, 1, W, 0];   // mirrored
    case 3: return [-1, 0, 0, -1, W, H];  // 180°
    case 4: return [1, 0, 0, -1, 0, H];   // mirrored vertically
    case 5: return [0, 1, 1, 0, 0, 0];    // transposed
    case 6: return [0, 1, -1, 0, W, 0];   // 90° clockwise
    case 7: return [0, -1, -1, 0, W, H];  // transverse
    case 8: return [0, -1, 1, 0, 0, H];   // 90° counter-clockwise
    default: return null;
  }
}

type Ctx2D = Pick<OffscreenCanvasRenderingContext2D, "save" | "restore" | "transform" | "drawImage">;

/** Draw `d` upright, scaled to fill a W×H canvas. */
export function drawUpright(ctx: Ctx2D, d: Drawable, W: number, H: number): void {
  const t = orientationTransform(d.orientation, W, H);
  if (!t) {
    ctx.drawImage(d.source, 0, 0, W, H);
    return;
  }
  ctx.save();
  ctx.transform(t[0], t[1], t[2], t[3], t[4], t[5]);
  const swap = swapsAxes(d.orientation);
  ctx.drawImage(d.source, 0, 0, swap ? H : W, swap ? W : H);
  ctx.restore();
}
